<template>
  <div v-if="contentTable.length > 0">
    <table class="custom-table table table-borderless">
      <thead>
        <tr>
          <th v-for="(head, index) in headList" :key="index" scope="col">
            {{ head }}
          </th>
          <th />
        </tr>
      </thead>
      <tbody>
        <tr v-for="(offer, index) in contentTable" :key="index">
          <td>{{ index + 1 }}</td>
          <td>
            <template v-if="offer.price">
              {{ +offer.priceWithoutTax.toFixed(2) }}
              {{ offer.currency | transformToCurrencyName }}
            </template>
            <template v-else>
              N/A
            </template>
          </td>
          <td>{{ offer.offerer | blockchainAddress }}</td>
          <td>{{ offer.expirationTime | formatSecondsToUtcTime }}</td>
          <td>
            <secondary-button
              v-if="isOwner"
              :is-disable="usersBalance[index] < offer.price"
              @onClick="openAcceptanceModal(offer)"
            >
              Accept
            </secondary-button>
            <secondary-button
              v-if="offer.offerer === myAddress"
              :is-disable="isCanceling"
              @onClick="cancelOffer"
            >
              <template v-if="isCanceling">
                <b-icon
                  icon="arrow-clockwise"
                  animation="spin"
                  font-scale="2"
                />
              </template>
              <template v-else>
                Cancel
              </template>
            </secondary-button>
          </td>
        </tr>
      </tbody>
    </table>
    <Pagination
      ref="paginate"
      class="market-pagination"
      :page-count="totalPage"
      :page="limit"
      @handlePageChange="handleChangePage"
    />
    <ConfirmAcceptanceModal
      ref="confirmAcceptModal"
      :offer="offer"
      :seller-address="sellerAddress"
      :nft="nft"
      :market-contract="marketContract"
    />
  </div>
  <div v-else>
    Offers Not Found
  </div>
</template>

<script>
import rf from '@/requests/RequestFactory';
import Pagination from '@/components/BaseComponents/CustomPagination.vue';
import {
  cancelOfferDutchAuction,
  cancelOfferListing,
  fetchUsersBalance
} from '@/helpers/utils';

import ConfirmAcceptanceModal from '@/modal/ConfirmAcceptanceModal.vue';
import SecondaryButton from '@/components/BaseComponents/SecondaryButton.vue';
import { handleTransactionError, sleep } from '@/helpers';
import BigNumber from 'bignumber.js';
import Vue from 'vue';
import {
  getMarketPriceOfNft,
  getDutchAuctionFinalPrice
} from '@/helpers/utils';
import { MARKET_ADDRESSES } from '@/constans';

const headList = ['No', 'Price', 'From', 'Expiration Time'];
export default {
  components: {
    Pagination,
    ConfirmAcceptanceModal,
    SecondaryButton
  },
  props: {
    sellerAddress: {
      type: String,
      default: ''
    },
    nft: {
      type: Object,
      default: () => ({})
    },
    marketContract: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      headList,
      contentTable: [],
      page: 1,
      limit: 10,
      totalPage: 1,
      isAcceptingOffer: false,
      isCanceling: false,
      usersBalance: [],
      offer: {}
    };
  },
  computed: {
    myAddress() {
      return this.$store.state.auth.address;
    },
    nftContract() {
      return this.nft.contract;
    },
    nftId() {
      return this.nft.id;
    },
    isOwner() {
      return this.sellerAddress === this.myAddress;
    },
    isDutchAuctionMarket() {
      return this.marketContract === MARKET_ADDRESSES.dutchAuctionMarketplace;
    }
  },
  mounted() {
    this.init();
  },
  methods: {
    handleTransactionError,
    handleChangePage(pageNum) {
      this.page = pageNum;
      this.init();
    },
    openAcceptanceModal(offer) {
      if (Date.now() >= offer.expirationTime * 1000) {
        this.$dialog.notify.error('Offer time expired');
        return;
      }
      Vue.set(offer, 'isAccepted', true);
      this.offer = offer;
      this.$bvModal.show('confirm-acceptance-modal');
    },
    async fetchData(currency) {
      const params = {
        page: this.page,
        limit: this.limit
      };
      const res = await rf
        .getRequest('GameMarketplaceRequest')
        .getOffers(this.nftContract, this.nftId, params);
      if (res) {
        this.contentTable = res.docs;
        this.totalPage = res.totalPages;
        this.page = res.page;
        const userAddresses = res.docs.map(item => item.offerer);
        const balanceData = await fetchUsersBalance(currency, userAddresses);
        if (balanceData) {
          this.usersBalance = balanceData.map(data =>
            new BigNumber(data[0]._hex).div(1e18).toNumber()
          );
        }
      }
    },
    async cancelOffer() {
      this.isCanceling = true;
      try {
        let tx;
        if (this.isDutchAuctionMarket) {
          tx = await cancelOfferDutchAuction(this.nftContract, this.nftId);
        } else {
          tx = await cancelOfferListing(this.nftContract, this.nftId);
        }
        if (tx.transactionHash) {
          await sleep(9000);
          this.$dialog.notify.success("You've canceled offer successfully!");
          this.init();
        } else {
          this.$dialog.notify.error('An error has occurred. Please try again!');
        }
      } catch (error) {
        this.isCanceling = false;
        const message = this.handleTransactionError(error);
        this.$dialog.notify.error(message);
      }
      this.isCanceling = false;
    },
    async getOnChainCurrentPrice() {
      if (!this.nftContract || !this.nftId) return;
      const [, currency] = await getMarketPriceOfNft(
        this.nftContract,
        this.nftId
      );
      return currency;
    },
    async getDutchAuctionFinalPrice() {
      if (!this.nftContract || !this.nftId) return;
      const [, currency] = await getDutchAuctionFinalPrice(
        this.nftContract,
        this.nftId
      );
      return currency;
    },
    async init() {
      let currency = '';
      if (this.isDutchAuctionMarket) {
        currency = await this.getDutchAuctionFinalPrice();
      } else {
        currency = await this.getOnChainCurrentPrice();
      }
      this.fetchData(currency);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/config/_variables';

.custom-table {
  background-color: #ffffff;
  border-radius: 6px;
  font-family: Inter;
  font-size: 16px;
  color: #203a54;
  thead {
    font-family: Inter-SemiBold;
    font-weight: 600;
    color: #828c9f;
    tr {
      th {
        background-color: #dfe2e8;
        &:first-child {
          border-top-left-radius: 6px;
        }
        &:last-child {
          border-top-right-radius: 6px;
        }
      }
    }
  }
  th,
  td {
    padding: 13px 22px;
  }
}
@media screen and (max-width: $breakpoint-desktop) {
  .custom-table {
    width: 100%;
    overflow-x: auto;
  }
}
</style>
